import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Banner, Icon, Spinner } from "@shopify/polaris";
import { ClipboardChecklistIcon } from "@shopify/polaris-icons";
import ProductCheckSettings from "./Components/ProductCheckSettings";

const ProductCheckSettingsPage = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const shop = urlParams.get("shop");

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planError, setPlanError] = useState("");

  useEffect(() => {
    fetchSettings();
  }, [shop]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shopify/product-check/settings?shop=${shop}`,
      );
      const data = await response.json();

      if (data.status) {
        setSettings(data.data.settings);
        setPlanError("");
      } else if (response.status === 403) {
        setPlanError("Product Check is available on Pro and Unlimited plans.");
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (newSettings) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shopify/product-check/settings?shop=${shop}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ settings: newSettings }),
        },
      );

      const data = await response.json();

      if (data.status) {
        setSettings(newSettings);
        setPlanError("");
        return true;
      } else if (response.status === 403) {
        setPlanError(
          "Upgrade to Pro or Unlimited to save Product Check settings.",
        );
        throw new Error("Plan upgrade required");
      } else {
        throw new Error(data.message || "Failed to save settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div>
        <div className="app-page-hero">
          <div>
            <div className="app-page-hero__eyebrow">
              <Icon source={ClipboardChecklistIcon} tone="base" />
              Quality gate
            </div>
            <h1 className="app-page-hero__title">Product Check</h1>
            <p className="app-page-hero__subtitle">
              Loading validation rules for Google Merchant uploads…
            </p>
          </div>
        </div>
        <div className="app-surface-card">
          <div
            className="app-surface-card__body"
            style={{ textAlign: "center", padding: 40 }}
          >
            <Spinner
              accessibilityLabel="Loading product check settings"
              size="large"
            />
          </div>
        </div>
      </div>
    );
  }

  if (planError) {
    return (
      <div>
        <div className="app-page-hero">
          <div>
            <div className="app-page-hero__eyebrow">
              <Icon source={ClipboardChecklistIcon} tone="base" />
              Quality gate
            </div>
            <h1 className="app-page-hero__title">Product Check</h1>
            <p className="app-page-hero__subtitle">
              Pre-upload validation for titles, media, collections, and blocked
              terms.
            </p>
          </div>
          <div className="app-page-hero__meta">
            <span className="app-chip app-chip--warn">Plan upgrade needed</span>
          </div>
        </div>
        <Banner tone="warning">{planError}</Banner>
      </div>
    );
  }

  return (
    <div>
      <div className="app-page-hero">
        <div>
          <div className="app-page-hero__eyebrow">
            <Icon source={ClipboardChecklistIcon} tone="base" />
            Quality gate
          </div>
          <h1 className="app-page-hero__title">Product Check</h1>
          <p className="app-page-hero__subtitle">
            Catch policy and data issues before products are uploaded to Google
            Merchant Center.
          </p>
        </div>
        <div className="app-page-hero__meta">
          <span className="app-chip app-chip--info">
            Pre-upload validation
          </span>
          {settings?.enabled ? (
            <span className="app-chip app-chip--success">Enabled</span>
          ) : (
            <span className="app-chip app-chip--warn">Disabled</span>
          )}
        </div>
      </div>

      <ProductCheckSettings
        shop={shop}
        onSave={handleSave}
        initialSettings={settings}
      />
    </div>
  );
};

export default ProductCheckSettingsPage;
